/**
 * Two-column layout shared by every research route. Fetches the article
 * index + categories once and provides them to children via React context.
 *
 * The sidebar's active category reflects the user's chosen filter
 * (`filterCategory`). It is intentionally NOT synced to the article
 * displayed on detail pages — navigating into an article shouldn't
 * change the user's filter selection on the list.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Container } from "@mui/material";

import CategoryRail, { ALL_CATEGORY_ID } from "./CategoryRail";
import { useArticles } from "./useArticles";
import {
  ResearchArticleIndexItem,
  ResearchCategory,
} from "../../../models/Research";
import { ArticleHeading } from "./headings";

interface ResearchLayoutContextValue {
  loading: boolean;
  error: string | null;
  categories: ResearchCategory[];
  articles: ResearchArticleIndexItem[];
  filterCategory: string;
  setFilterCategory: (id: string) => void;
  /** ArticleDetail populates this with the headings it extracts from
   *  body_md; CategoryRail reads it to render the "In this article" list.
   *  Cleared (empty array) on detail unmount. */
  setTocHeadings: (headings: ArticleHeading[]) => void;
  /** Re-fetch the article index + categories. Called by the editor
   *  after every successful create / update / publish / unpublish /
   *  delete so the rail and the list page reflect the change without
   *  a full reload. */
  refetchIndex: () => void;
}

const ResearchLayoutContext = createContext<ResearchLayoutContextValue | null>(
  null,
);

export function useResearchLayoutContext(): ResearchLayoutContextValue {
  const ctx = useContext(ResearchLayoutContext);
  if (!ctx) {
    throw new Error(
      "useResearchLayoutContext must be used inside a ResearchLayout route",
    );
  }
  return ctx;
}

export default function ResearchLayout() {
  const {
    loading,
    error,
    categories,
    articles,
    refetch: refetchIndex,
  } = useArticles();
  const navigate = useNavigate();
  const location = useLocation();

  const [filterCategory, setFilterCategory] =
    useState<string>(ALL_CATEGORY_ID);
  const [tocHeadings, setTocHeadings] = useState<ArticleHeading[]>([]);

  const recentlyUpdated = useMemo(() => articles.slice(0, 5), [articles]);

  const onSelectCategory = (id: string) => {
    setFilterCategory(id);
    // From a detail page, jump back to the list so the filter is visible.
    if (location.pathname !== "/research") {
      navigate("/research");
    }
  };

  const ctxValue: ResearchLayoutContextValue = {
    loading,
    error,
    categories,
    articles,
    filterCategory,
    setFilterCategory,
    setTocHeadings,
    refetchIndex,
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
      >
        <CategoryRail
          categories={categories}
          selected={filterCategory}
          onSelect={onSelectCategory}
          recentlyUpdated={recentlyUpdated}
          onSelectArticle={(slug) => navigate(`/research/${slug}`)}
          loading={loading}
          tocHeadings={tocHeadings}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ResearchLayoutContext.Provider value={ctxValue}>
            <Outlet />
          </ResearchLayoutContext.Provider>
        </Box>
      </Box>
    </Container>
  );
}
